/* ============================================================================
   HOSTED FLOW BETA — loceng notifikasi (header Workspace)
   ============================================================================
   Pelanggan Submit batch video → tutup browser. Bila SATU batch selesai diproses,
   server cipta satu notifikasi (userNotifications/{uid}/items/{batchId}). Loceng ini
   memaparkannya dan KEKAL selepas refresh / login device lain (dibaca dari server,
   bukan memori sesi). Klik → tanda dibaca + bawa ke Workspace.

   Reka bentuk: v3.9 monokrom (tiada emoji). Data milik UID sendiri sahaja — dibaca
   melalui endpoint /notifications yang server skop ikut token (lihat flowWorkerHttp).
   Backend mati → senyap (loceng kekal data terakhir; TIDAK memaparkan status palsu).
   i18n: kunci hosted.notif.* (ms.js + en.js).
   ========================================================================== */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useT } from '../i18n';
import { bacaNotifikasiHostedBeta, tandaNotifikasiDibaca } from '../services/centralWorkerService';

export default function HostedNotifBell({ onNavigate }) {
  const t = useT();
  const [items, setItems] = useState([]);
  const [buka, setBuka] = useState(false);
  const [dimuat, setDimuat] = useState(false);
  const wrapRef = useRef(null);

  const muat = useCallback(async () => {
    try { const r = await bacaNotifikasiHostedBeta(30); setItems(Array.isArray(r.notifications) ? r.notifications : []); setDimuat(true); }
    catch { /* senyap — backend tiada: kekalkan data terakhir, JANGAN fabrikasi */ }
  }, []);

  // Muat pada mount + berkala + bila tab difokus semula (customer balik selepas tutup browser).
  useEffect(() => {
    let stop = false;
    const tick = () => { if (!stop) muat(); };
    tick();
    const tmr = setInterval(tick, 20000);
    const onFocus = () => tick();
    window.addEventListener('focus', onFocus);
    return () => { stop = true; clearInterval(tmr); window.removeEventListener('focus', onFocus); };
  }, [muat]);

  // Tutup dropdown bila klik luar.
  useEffect(() => {
    if (!buka) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setBuka(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [buka]);

  const belumBaca = items.filter((n) => !n.read).length;

  const bukaNotif = async (n) => {
    if (!n.read) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x))); // optimis
      try { await tandaNotifikasiDibaca(n.id); } catch { /* senyap — muat berikutnya betulkan */ }
    }
    setBuka(false);
    if (onNavigate) onNavigate('workspace');
  };

  const ringkas = (n) => {
    const siap = `${n.berjaya || 0}/${n.total || 0} ${t('hosted.notif.videoSiap')}`;
    const extra = [];
    if (n.perluSemakan) extra.push(`${n.perluSemakan} ${t('hosted.notif.perluSemak')}`);
    if (n.gagal) extra.push(`${n.gagal} ${t('hosted.notif.gagal')}`);
    return { tajuk: siap, sub: extra.join(' · ') };
  };

  // Jangan render apa-apa sebelum muatan pertama berjaya (elak loceng kosong berkelip untuk bukan-pelanggan).
  if (!dimuat && items.length === 0) return null;

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button" onClick={() => setBuka((v) => !v)} aria-label={t('hosted.notif.title')}
        style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 8, border: '1px solid var(--s-line)', background: 'transparent', color: 'var(--s-ink-2, #556)', cursor: 'pointer' }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 19 }}>notifications</span>
        {belumBaca > 0 && (
          <span style={{ position: 'absolute', top: -5, right: -5, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 999, background: 'var(--s-ink, #223)', color: 'var(--s-bg, #fff)', fontSize: 10.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            {belumBaca > 9 ? '9+' : belumBaca}
          </span>
        )}
      </button>

      {buka && (
        <div style={{ position: 'absolute', top: 40, right: 0, width: 290, maxHeight: 360, overflowY: 'auto', background: 'var(--s-bg, #fff)', border: '1px solid var(--s-line)', borderRadius: 10, boxShadow: '0 8px 28px rgba(0,0,0,.12)', zIndex: 60 }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--s-line)', fontSize: 12.5, fontWeight: 700, color: 'var(--s-ink, #223)' }}>
            {t('hosted.notif.title')}
          </div>
          {items.length === 0 ? (
            <div style={{ padding: '18px 12px', fontSize: 12, color: 'var(--s-ink-3, #889)', textAlign: 'center' }}>{t('hosted.notif.kosong')}</div>
          ) : (
            items.map((n) => {
              const r = ringkas(n);
              return (
                <button key={n.id} type="button" onClick={() => bukaNotif(n)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--s-line)', background: n.read ? 'transparent' : 'var(--s-ink-7, #f4f5f8)', border: 'none', borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: 'var(--s-line)', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {!n.read && <span style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--s-ink, #223)', flex: 'none' }} />}
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--s-ink, #223)' }}>{t('hosted.notif.batchTajuk')}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--s-ink-2, #556)', marginTop: 2 }}>{r.tajuk}</div>
                  {r.sub && <div style={{ fontSize: 11.5, color: 'var(--s-warn, #b26a00)', marginTop: 1 }}>{r.sub}</div>}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
